import { db } from '../config/firebase.js';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';

export const getCmtHierarchy = async (cmtId) => {
  try {
    // First check if we can access this CMT
    const cmtRef = doc(db, 'c3_cmts', cmtId);
    const cmtDoc = await getDoc(cmtRef);
    
    if (!cmtDoc.exists()) {
      console.log(`CMT ${cmtId} not found`);
      return null;
    }

    const cmtData = cmtDoc.data();
    const hierarchy = {
      id: cmtId,
      ...cmtData,
      children: []
    };

    // Get child CMTs - only get direct children that the user has access to
    const childCmtsQuery = query(
      collection(db, 'c3_cmts'),
      where('parentCmtId', '==', cmtId)
    );
    const childCmtsSnapshot = await getDocs(childCmtsQuery);
    
    // For each child CMT, check if user has access before adding to hierarchy
    for (const childDoc of childCmtsSnapshot.docs) {
      try {
        const childHierarchy = await getCmtHierarchy(childDoc.id);
        if (childHierarchy) {
          hierarchy.children.push(childHierarchy);
        }
      } catch (error) {
        // Skip CMTs that user doesn't have access to
        console.log(`Skipping CMT ${childDoc.id} due to access restrictions`);
        continue;
      }
    }

    return hierarchy;
  } catch (error) {
    console.error('Error getting CMT hierarchy:', error);
    return null;
  }
};

export const getUserCmts = async (userId) => {
  try {
    const userRef = doc(db, 'c3_users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    const userData = userDoc.data();
    
    // Get the user's orgId
    const orgId = userData.orgId;
    
    // Filter out any CMT IDs that match the orgId
    const cmtIds = Array.isArray(userData.cmtIds) ? 
      userData.cmtIds.filter(id => id !== orgId) : [];
    const accessibleCmts = Array.isArray(userData.accessibleCmts) ? 
      userData.accessibleCmts.filter(id => id !== orgId) : [];
    
    // Return unique CMT IDs
    return [...new Set([...cmtIds, ...accessibleCmts])];
  } catch (error) {
    console.error('Error getting user CMTs:', error);
    throw error;
  }
};

export const canAccessCmt = async (userId, cmtId) => {
  try {
    const userCmts = await getUserCmts(userId);
    console.log('userCmts:', userCmts, 'cmtId:', cmtId);
    // If user has direct access to the CMT
    if (Array.isArray(userCmts) && userCmts.includes(cmtId)) {
      return true;
    }

    // Check if user has access to any parent CMTs
    const cmtRef = doc(db, 'c3_cmts', cmtId);
    const cmtDoc = await getDoc(cmtRef);
    
    if (!cmtDoc.exists()) {
      return false;
    }

    const cmtData = cmtDoc.data();
    if (!cmtData.parentCmtId) {
      return false;
    }

    // Recursively check parent CMTs
    return canAccessCmt(userId, cmtData.parentCmtId);
  } catch (error) {
    console.error('Error checking CMT access:', error);
    return false;
  }
};

export const getAllAccessibleCmts = async (userId) => {
  try {
    const userCmts = await getUserCmts(userId);
    const accessibleCmts = new Set(userCmts);

    // For each CMT the user has direct access to, get all child CMTs
    for (const cmtId of userCmts) {
      try {
        const hierarchy = await getCmtHierarchy(cmtId);
        const getAllChildIds = (cmt) => {
          cmt.children.forEach(child => {
            accessibleCmts.add(child.id);
            getAllChildIds(child);
          });
        };
        getAllChildIds(hierarchy);
      } catch (error) {
        // Skip CMTs that user doesn't have access to
        console.log(`Skipping CMT ${cmtId} due to access restrictions`);
        continue;
      }
    }

    return Array.from(accessibleCmts);
  } catch (error) {
    console.error('Error getting accessible CMTs:', error);
    throw error;
  }
};

export const getCmtAccessLevel = async (userId, cmtId) => {
  try {
    const userRef = doc(db, 'c3_users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) return null;
    
    const userData = userDoc.data();
    const cmtRef = doc(db, 'c3_cmts', cmtId);
    const cmtDoc = await getDoc(cmtRef);
    
    if (!cmtDoc.exists()) return null;
    
    const cmtData = cmtDoc.data();
    
    // Check if user has direct access to this CMT
    const hasDirectAccess = userData.cmtIds.includes(cmtId) || 
                           userData.accessibleCmts.includes(cmtId);
    
    if (hasDirectAccess) {
      return {
        level: 'direct',
        role: userData.role,
        cmtData
      };
    }
    
    // Check parent CMTs
    let currentCmt = cmtData;
    while (currentCmt.parentCmtId) {
      const parentRef = doc(db, 'c3_cmts', currentCmt.parentCmtId);
      const parentDoc = await getDoc(parentRef);
      
      if (!parentDoc.exists()) break;
      
      const parentData = parentDoc.data();
      const hasParentAccess = userData.cmtIds.includes(parentData.id) || 
                             userData.accessibleCmts.includes(parentData.id);
      
      if (hasParentAccess) {
        return {
          level: 'inherited',
          role: userData.role,
          cmtData: parentData
        };
      }
      
      currentCmt = parentData;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting CMT access level:', error);
    return null;
  }
};

export const updateCmtHierarchy = async (cmtId, updates) => {
  try {
    const cmtRef = doc(db, 'c3_cmts', cmtId);
    const cmtDoc = await getDoc(cmtRef);
    
    if (!cmtDoc.exists()) {
      throw new Error('CMT not found');
    }
    
    const currentData = cmtDoc.data();
    const newData = {
      ...currentData,
      ...updates,
      updatedAt: new Date()
    };
    
    // If changing parent, validate the new parent exists
    if (updates.parentCmtId && updates.parentCmtId !== currentData.parentCmtId) {
      const parentRef = doc(db, 'c3_cmts', updates.parentCmtId);
      const parentDoc = await getDoc(parentRef);
      
      if (!parentDoc.exists()) {
        throw new Error('Parent CMT not found');
      }
      
      // Prevent circular references
      let currentParent = parentDoc.data();
      while (currentParent.parentCmtId) {
        if (currentParent.parentCmtId === cmtId) {
          throw new Error('Circular reference detected in CMT hierarchy');
        }
        const nextParentRef = doc(db, 'c3_cmts', currentParent.parentCmtId);
        const nextParentDoc = await getDoc(nextParentRef);
        if (!nextParentDoc.exists()) break;
        currentParent = nextParentDoc.data();
      }
    }
    
    await updateDoc(cmtRef, newData);
    return newData;
  } catch (error) {
    console.error('Error updating CMT hierarchy:', error);
    throw error;
  }
}; 